import { useState, useEffect } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Download,
  Eye,
  EyeOff,
  AlertCircle,
  Image as ImageIcon,
  FileText,
} from 'lucide-react';
import { fetchApi } from '@/lib/api';

// Mirroring Go backend structures (subset needed)
interface Comment {
  id: number;
  guest_name: string;
  comment_text: string;
  commented_at: string;
}

interface BaseEntry {
  id: number;
  event_id: number;
  guest_name: string;
  guest_email: string;
  media_type: 'photo' | 'text';
  content: string;
  caption: string | null;
  is_private: boolean;
  uploaded_at: string;
}

interface AdminEntryResponse extends BaseEntry {
  like_count: number;
  comments: Comment[];
  relative_path?: string; // Path relative to /uploads/events base
}

export const Route = createFileRoute('/_authenticated/events/$eventId/gallery')({
  component: AdminEventGallery,
});

function AdminEventGallery() {
  const { eventId } = useParams({ from: '/_authenticated/events/$eventId/gallery' });
  const [entries, setEntries] = useState<AdminEntryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventDetails, setEventDetails] = useState<{ name: string } | null>(
    null,
  ); // Optional: Fetch event name

  const parsedEventId = parseInt(eventId, 10);
  const isValidEventId = !isNaN(parsedEventId);

  // --- Data Fetching ---
  useEffect(() => {
    if (!isValidEventId) {
        setError('Invalid Event ID provided in URL.');
        setIsLoading(false);
        return;
    }

    const fetchEventDetails = async () => {
      try {
        const res = await fetchApi(`/api/admin/events/${parsedEventId}`);
        if (!res.ok) {
          throw new Error(`Failed to fetch event details: ${res.statusText} (Status: ${res.status})`);
        }
        const data = await res.json();
        setEventDetails(data);
      } catch (err) {
        console.error('Error fetching event details:', err);
      }
    };

    const fetchAdminEntries = async () => {
      setIsLoading(true);
      setError(null);
      setEntries([]); // Clear previous entries

      try {
        const response = await fetchApi(
          `/api/admin/events/${parsedEventId}/entries`,
        );
        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Access Denied: You do not own this event.');
          } else if (response.status === 404) {
             console.log(`No entries found for event ${parsedEventId}.`);
          } else {
              throw new Error(`Failed to fetch entries: ${response.statusText} (Status: ${response.status})`);
          }
        }
        if (response.ok && response.status !== 404) {
        const data: AdminEntryResponse[] = await response.json();
        setEntries(data);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'An unknown error occurred while fetching entries.',
        );
        console.error('Error fetching admin entries:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventDetails();
    fetchAdminEntries();
  }, [parsedEventId, isValidEventId]);

  // --- Event Handlers ---
  const handlePrivacyToggle = async (entryId: number, checked: boolean) => {
    if (!isValidEventId) return;
    const newIsPrivate = checked;
    // Optimistic UI update
    setEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId ? { ...entry, is_private: newIsPrivate } : entry,
      ),
    );

    try {
      const response = await fetchApi(`/api/admin/entries/${entryId}/privacy`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_private: newIsPrivate }),
      });

      if (!response.ok) {
          const errorText = await response.text();
        // Revert UI on error
        setEntries((prevEntries) =>
          prevEntries.map((entry) =>
            entry.id === entryId
              ? { ...entry, is_private: !newIsPrivate }
              : entry,
          ),
        );
        console.error(
            `Failed to update privacy status: ${response.status} - ${errorText}`,
        );
        setError(
              `Failed to update privacy for entry ${entryId}. Server responded: ${errorText || response.statusText}`
        );
          setTimeout(() => setError(null), 5000);
      } else {
        setError(null); // Clear previous errors on success
      }
    } catch (err) {
      // Revert UI on error
      setEntries((prevEntries) =>
        prevEntries.map((entry) =>
          entry.id === entryId
            ? { ...entry, is_private: !newIsPrivate }
            : entry,
        ),
      );
       const errorMessage = `Failed to update privacy for entry ${entryId}: ${err instanceof Error ? err.message : 'Network error'}`;
      setError(errorMessage);
      console.error('Error updating privacy:', err);
      setTimeout(() => setError(null), 5000);
    }
  };

  // --- Render Logic ---
  if (!isValidEventId) {
     return (
         <div className="container mx-auto p-4">
             <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Invalid Event ID</AlertTitle>
                <AlertDescription>
                    The event ID in the URL is not valid.
                </AlertDescription>
            </Alert>
         </div>
     );
  }

  const renderGallery = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
              <CardFooter className="flex justify-between items-center">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-8" />
              </CardFooter>
            </Card>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    if (entries.length === 0) {
      return (
        <p className="text-center text-gray-500 dark:text-gray-400">
          No entries found for this event yet.
        </p>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {entries.map((entry) => (
          <Card
            key={entry.id}
            className={`relative ${entry.is_private ? 'border-orange-500 border-2' : ''}`}
          >
            {entry.is_private && (
              <Badge
                variant="secondary"
                className="absolute top-2 right-2 z-10 bg-orange-100 text-orange-800"
              >
                <EyeOff className="h-3 w-3 mr-1" /> Private
              </Badge>
            )}
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {entry.media_type === 'photo' ? (
                  <ImageIcon className="h-5 w-5 text-blue-500" />
                ) : (
                  <FileText className="h-5 w-5 text-green-500" />
                )}
                Entry #{entry.id}
              </CardTitle>
              <CardDescription>
                By {entry.guest_name} ({entry.guest_email})<br />
                {formatDistanceToNow(new Date(entry.uploaded_at), {
                  addSuffix: true,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {entry.media_type === 'photo' && entry.relative_path && (
                <img
                  src={`/uploads/events/${entry.relative_path}`}
                  alt={
                    entry.caption
                      ? entry.caption
                      : `Photo by ${entry.guest_name}`
                  }
                  className="w-full h-40 object-cover rounded-md mb-2 bg-gray-200"
                  loading="lazy"
                  onError={(e) => {
                    // Handle image loading errors, e.g., show a placeholder
                    e.currentTarget.src =
                      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // Transparent pixel
                    e.currentTarget.alt = 'Image failed to load';
                  }}
                />
              )}
              {entry.media_type === 'text' && (
                <p className="text-sm bg-gray-100 dark:bg-gray-800 p-2 rounded h-40 overflow-auto">
                  {entry.content}
                </p>
              )}
              {entry.caption && (
                <p className="text-sm italic mt-2">
                  &quot;{entry.caption}&quot;
                </p>
              )}
            </CardContent>
            <CardFooter className="flex flex-col items-start space-y-3">
              <div className="flex items-center space-x-2 w-full">
                <Switch
                  id={`privacy-toggle-${entry.id}`}
                  checked={entry.is_private}
                  onCheckedChange={(checked: boolean) =>
                    handlePrivacyToggle(entry.id, checked)
                  }
                />
                <Label
                  htmlFor={`privacy-toggle-${entry.id}`}
                  className="flex items-center cursor-pointer"
                >
                  {entry.is_private ? (
                    <EyeOff className="h-4 w-4 mr-1" />
                  ) : (
                    <Eye className="h-4 w-4 mr-1" />
                  )}
                  {entry.is_private ? 'Private' : 'Public'}
                </Label>
              </div>
              {entry.media_type === 'photo' && (
                <Button asChild variant="outline" size="sm">
                  {/* Use <a> tag for direct download */}
                  <a href={`/api/admin/entries/${entry.id}/download`} download>
                    <Download className="mr-2 h-4 w-4" /> Download
                  </a>
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Admin Gallery
          {eventDetails
            ? `: ${eventDetails.name}`
            : eventId
              ? ` for Event #${eventId}`
              : ''}
        </h1>
        {entries.some((e) => e.media_type === 'photo') && (
          <Button asChild>
            <a
              href={`/api/admin/events/${parsedEventId}/download-all`}
              download
            >
              <Download className="mr-2 h-4 w-4" /> Download All Photos
            </a>
          </Button>
        )}
      </div>

      {renderGallery()}
    </div>
  );
}
